import { useState, type InputHTMLAttributes, type ReactNode } from "react";
import { ArrowLeft, Check, Eye, EyeOff } from "lucide-react";
import "../../styles/auth-design.css";

export function PasswordField({ label, hint, id, ...props }: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string; id: string }) {
  const [visible, setVisible] = useState(false);
  return <div className="ja-field">
    <label htmlFor={id}>{label}</label>
    <div className="ja-password">
      <input {...props} id={id} type={visible ? "text" : "password"} aria-describedby={hint ? `${id}-hint` : props["aria-describedby"]} />
      <button type="button" className="ja-password-toggle" disabled={props.disabled} onClick={() => setVisible(v => !v)} aria-label={`${label} ${visible ? "숨기기" : "표시"}`} aria-pressed={visible}>
        {visible ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
    {hint && <small id={`${id}-hint`} className="ja-hint">{hint}</small>}
  </div>;
}

export default function AuthFrame({ title, heading, description, onBack, children, steps, step = 0, completed = false, busy = false, progressLabel = "회원가입" }: {
  title: string; heading?: ReactNode; description?: ReactNode; onBack: () => void; children: ReactNode;
  steps?: string[]; step?: number; completed?: boolean; busy?: boolean; progressLabel?: string;
}) {
  const asideSteps = steps || ["결제 정보", "본인 확인", "받는 분 정보"];
  const activeStep = steps ? step : 1;
  return <main className="ja-page">
    <div className="ja-layout">
      <aside className="ja-aside">
        <h1>주류대금도,<br />결제는 <em>더 여유롭게.</em></h1>
        <ol className="ja-steps" aria-label={steps ? `${progressLabel} 순서` : "신청 순서"}>{asideSteps.map((label, index) => <li key={label} className={completed || index < activeStep ? "is-done" : index === activeStep ? "is-current" : ""} aria-current={!completed && index === activeStep ? "step" : undefined}>
          <span>{completed || index < activeStep ? <Check size={15} /> : `0${index + 1}`}</span><div>{label}</div>
        </li>)}</ol>
      </aside>
      <section className="ja-card" aria-label={title}>
        <div className="ja-topbar">
          <button type="button" onClick={onBack} disabled={busy} aria-label="이전으로"><ArrowLeft size={20} /></button>
          <strong>{title}</strong><span>{steps ? completed ? "완료" : `${step + 1} / ${steps.length}` : ""}</span>
        </div>
        {steps && <div className="ja-progress" role="progressbar" aria-label={`${progressLabel} 진행`} aria-valuemin={0} aria-valuemax={steps.length} aria-valuenow={completed ? steps.length : step + 1}>{steps.map((label, index) => <span key={label} className={completed || index <= step ? "is-active" : ""} />)}</div>}
        <div className="ja-body">
          {heading && <h2>{heading}</h2>}
          {description && <p className="ja-description">{description}</p>}
          {children}
        </div>
      </section>
    </div>
  </main>;
}
